import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Brain, Shield, FileCheck } from 'lucide-react'; 
import { useTypewriter } from '../../hooks/useTypewriter';
import AnimatedBackground from '../../components/AnimatedBackground';

const values = [
  {
    icon: Target,
    title: 'Accuracy First',
    description: 'Every algorithm is calibrated against official board standards to ensure zero hallucinations. No approximations.'
  },
  {
    icon: Brain,
    title: 'Structured Intelligence',
    description: 'Raw AI output is meaningless. We structure insights into actionable data: marks lost, gap identified, fix provided.'
  },
  {
    icon: Shield,
    title: 'Privacy by Design',
    description: 'Student data is encrypted, anonymized, and never sold. Your exam papers remain your private property.'
  },
  {
    icon: FileCheck, 
    title: 'Engineered for Exams',
    description: 'We don\'t use generic chat models. We fine-tune our engine to replicate the strict grading logic of a board examiner.'
  }
];

const stats = [
  { value: '10+', label: 'Boards Supported' },
  { value: '< 60s', label: 'Analysis Speed' },
  { value: '24/7', label: 'AI Tutor Access' },
  { value: '100%', label: 'Syllabus Coverage' }
];

// Animated counter component - STATIC HOVER
function AnimatedStat({ value, label, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div 
      ref={ref}
      className="text-center p-6 rounded-xl bg-background border border-border transition-colors"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay }}
    >
      <motion.p 
        className="text-3xl md:text-4xl font-bold text-primary mb-1"
        animate={isInView ? { scale: [0.5, 1.1, 1] } : {}}
        transition={{ delay: delay + 0.2, duration: 0.5 }}
      >
        {value}
      </motion.p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </motion.div>
  );
}

// Value card - STATIC HOVER & ICONS
function ValueCard({ value, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const Icon = value.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      data-testid={`value-${index}`}
      className="group p-6 rounded-xl bg-background border border-border transition-all duration-300"
    >
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-colors relative">
        <Icon className="w-6 h-6 text-primary relative z-10" />
      </div>
      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{value.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
    </motion.div>
  );
}

export default function AboutSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
   
  const { displayText, isTyping } = useTypewriter(
    'Engineered to think like an examiner, not just an AI.',
    40,
    500
  );

  return (
    <section 
      ref={sectionRef}
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-32 bg-card/50 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-20">
        <AnimatedBackground variant="neural" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - STATIC */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Brain className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">About Edalyze</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 max-w-3xl mx-auto min-h-[80px]">
            <span className="font-mono text-primary">
              {displayText}
              {isTyping && (
                <motion.span
                  className="inline-block w-0.5 h-[1em] bg-primary ml-1 align-middle"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
            </span>
          </h2>
          
          <motion.p 
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
          >
            Edalyze was founded with a simple belief: students deserve to know exactly <strong>why</strong> they lose marks, not just that they lost them. We combine board-compliant logic with precision AI to deliver insights that actually improve performance.
          </motion.p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {values.map((value, index) => (
            <ValueCard key={index} value={value} index={index} />
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <AnimatedStat 
              key={index} 
              value={stat.value} 
              label={stat.label} 
              delay={0.4 + index * 0.1} 
            />
          ))}
        </div>

        {/* Mission Statement - STATIC */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            <span className="text-foreground font-medium">"We don't believe in AI hype.</span> We believe in exam-authentic logic, board-calibrated accuracy, and insights that actually help students improve — one question at a time."
          </p>
        </motion.div>
      </div>
    </section>
  );
}
